import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";

const fall = keyframes`
  0% {
    transform: translateY(-100vh) translateX(0px) rotate(0deg);
    opacity: 1;
  }
  25% {
    transform: translateY(-75vh) translateX(${(props) =>
      props.wind1}px) rotate(90deg);
    opacity: 1;
  }
  50% {
    transform: translateY(-50vh) translateX(${(props) =>
      props.wind2}px) rotate(180deg);
    opacity: 0.9;
  }
  75% {
    transform: translateY(-25vh) translateX(${(props) =>
      props.wind3}px) rotate(270deg);
    opacity: 0.7;
  }
  100% {
    transform: translateY(120vh) translateX(${(props) =>
      props.wind4}px) rotate(360deg);
    opacity: 0;
  }
`;

const ConfettiContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
`;

const ConfettiPiece = styled.div`
  position: absolute;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size * 0.6}px;
  background: ${(props) => props.color};
  top: -10px;
  left: ${(props) => props.left}%;
  animation: ${fall} ${(props) => props.duration}s
    cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: ${(props) => props.delay}s;
  border-radius: 3px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transform-origin: center;
`;

const ConfettiBurst = ({ onComplete }) => {
  const [confettiPieces, setConfettiPieces] = useState([]);

  useEffect(() => {
    // LandlordOps darker green variants only
    const brandColors = [
      "#2C5F2D", // Primary forest green
      "#1E3D1F", // Dark forest green
      "#0f230f", // Darker forest green
      "#4A7C59", // Light forest green
      "#3A6B4A", // Medium forest green
      "#2A5A3A", // Medium-dark forest green
    ];

    const pieces = [];
    const numPieces = 250; // More pieces for better coverage
    const duration = 4; // 4 seconds total

    for (let i = 0; i < numPieces; i++) {
      // Generate wind values for realistic drift
      const wind1 = (Math.random() - 0.5) * 40; // -20 to 20px drift
      const wind2 = wind1 + (Math.random() - 0.5) * 30; // Continue drift
      const wind3 = wind2 + (Math.random() - 0.5) * 20; // More drift
      const wind4 = wind3 + (Math.random() - 0.5) * 10; // Final drift

      pieces.push({
        id: i,
        left: Math.random() * 100,
        color: brandColors[Math.floor(Math.random() * brandColors.length)],
        size: Math.random() * 12 + 8, // 8-20px width (bigger pieces)
        duration: Math.random() * 2 + 3, // 3-5 seconds
        delay: Math.random() * 0.3, // Small stagger for more natural effect
        wind1,
        wind2,
        wind3,
        wind4,
      });
    }

    setConfettiPieces(pieces);

    // Clean up after animation completes
    const cleanup = setTimeout(
      () => {
        setConfettiPieces([]);
        if (onComplete) {
          onComplete();
        }
      },
      (duration + 2) * 1000
    );

    return () => clearTimeout(cleanup);
  }, [onComplete]);

  if (confettiPieces.length === 0) {
    return null;
  }

  return (
    <ConfettiContainer>
      {confettiPieces.map((piece) => (
        <ConfettiPiece
          key={piece.id}
          left={piece.left}
          color={piece.color}
          size={piece.size}
          duration={piece.duration}
          delay={piece.delay}
          wind1={piece.wind1}
          wind2={piece.wind2}
          wind3={piece.wind3}
          wind4={piece.wind4}
        />
      ))}
    </ConfettiContainer>
  );
};

export default ConfettiBurst;
